import type { IconName } from "@/lib/icons";
import type { FAQ } from "@/content/solutions";

// Our actual customer journey (§9 of the update spec). No weekly-tuning or
// recurring-service claims — just how a customer goes from discovery to live.
export const howItWorks: { step: number; title: string; body: string }[] = [
  {
    step: 1,
    title: "You find Quto",
    body: "You discover Quto AI through search or our website and see exactly what an agent can do for you.",
  },
  {
    step: 2,
    title: "Tell us your goal",
    body: "Tell us what you want the agent to handle for your business — in plain language, no spec sheet needed.",
  },
  {
    step: 3,
    title: "We build your agent",
    body: "Based on your requirement, we design and configure the right AI agent for your use case.",
  },
  {
    step: 4,
    title: "We deploy it with you",
    body: "We implement and take it live on your channels and website — together, hands-on.",
  },
];

export const securityPoints: { icon: IconName; title: string; body: string }[] = [
  {
    icon: "Server",
    title: "Data stays in your infra",
    body: "Run agents in or against your own infrastructure where possible.",
  },
  {
    icon: "Lock",
    title: "No training on your data",
    body: "We don't train models on your conversations or customer data.",
  },
  {
    icon: "FileText",
    title: "DPA available",
    body: "Sign a data processing agreement before we handle anything.",
  },
  {
    icon: "ShieldCheck",
    title: "Access controls",
    body: "Scoped access and audit trails for every integration.",
  },
];

export const securityBadges = [
  "GDPR-aware",
  "India DPDP-aware",
  "DPA available",
  "Human handoff",
];

export const homeFaqs: FAQ[] = [
  {
    q: "What is an AI agent?",
    a: "An AI agent is software that holds real conversations — over voice, WhatsApp, chat or your website — and takes action, like booking a meeting or updating your CRM. It's more than a chatbot: it understands context and follows through.",
  },
  {
    q: "Which channels do you support?",
    a: "Voice, WhatsApp, chat and web today — with the same agent able to work across all of them.",
  },
  {
    q: "How long does it take to go live?",
    a: "Most agents go live in a few weeks. The timeline depends on your channels, integrations and how much custom logic you need — we scope it with you on the first call.",
  },
  {
    q: "Do you replace my team?",
    a: "No. Agents handle the repetitive, round-the-clock work and hand off to your team the moment a conversation needs a human.",
  },
  {
    q: "How is pricing structured?",
    a: "You pay for setup plus usage, not seats. We match the engagement to your volume and channels — book a call for a custom quote.",
  },
  {
    q: "Is my data safe?",
    a: "Your data stays in your infrastructure where possible, we don't train on it, and a DPA is available. We're GDPR- and India DPDP-aware.",
  },
];
